import { useQuery } from '@tanstack/react-query';
import type {
  IUrineLevelDistributionByTimeItem,
  IUrineLevelDistributionItem,
  IUrineLevelDistributionPayload,
} from '../../interfaces/Analysis/IAnalysis';
import { STALE_TIME_DETAIL_MS, STALE_TIME_LIST_MS } from '../../constants/reactQueryStaleTime.ts';
import AnalysisService from '../../services/AnalysisService';
import CareHomeService from '../../services/CareHomeService';
import DiaperService from '../../services/DiaperService';
import ResidentService from '../../services/ResidentService';
import StationService from '../../services/StationService';

export type AnalysisFacilityOption = { id: string; name: string };
export type AnalysisStationOption = { id: string; name: string };
export type AnalysisResidentOption = { id: string };

export const getAnalysisCareHomesQueryKey = () => ['analysisCareHomes'] as const;

export function useAnalysisCareHomesQuery() {
  return useQuery<AnalysisFacilityOption[]>({
    queryKey: getAnalysisCareHomesQueryKey(),
    queryFn: async () => {
      const response = await CareHomeService.getCareHomes({
        orderBy: 'name',
        sortOrder: 'asc',
        is_active: true,
      });
      return (response.items ?? []).map((careHome) => ({
        id: careHome.id,
        name: careHome.name,
      }));
    },
    staleTime: STALE_TIME_LIST_MS,
  });
}

export const getAnalysisStationsQueryKey = (careHomeId?: string) =>
  ['analysisStations', careHomeId] as const;

export function useAnalysisStationsQuery(careHomeId?: string) {
  return useQuery<AnalysisStationOption[]>({
    queryKey: getAnalysisStationsQueryKey(careHomeId),
    enabled: !!careHomeId && careHomeId !== '1',
    queryFn: async () => {
      const response = await StationService.getListStations(
        1,
        100,
        '',
        careHomeId,
        'true',
        'name',
        'asc'
      );
      return (response.items ?? []).map((station) => ({
        id: station.id,
        name: station.name,
      }));
    },
    staleTime: STALE_TIME_LIST_MS,
  });
}

export const getAnalysisResidentsQueryKey = (
  careHomeId?: string,
  stationIds?: string
) => ['analysisResidents', careHomeId, stationIds] as const;

export function useAnalysisResidentsQuery(
  careHomeId?: string,
  stationIds?: string,
  enabled?: boolean
) {
  return useQuery<AnalysisResidentOption[]>({
    queryKey: getAnalysisResidentsQueryKey(careHomeId, stationIds),
    enabled: !!enabled && !!careHomeId && careHomeId !== '1',
    queryFn: async () => {
      const response = await ResidentService.getResidentList({
        page: 1,
        per_page: 100,
        carehome_id: careHomeId,
        station_id: stationIds,
        orderBy: 'first_name',
        sortOrder: 'asc',
        is_active: true,
      });
      return (response.items ?? []).map((resident) => ({ id: resident.id }));
    },
    staleTime: STALE_TIME_LIST_MS,
  });
}

export const getAnalysisResidentNameQueryKey = (residentId?: string) =>
  ['analysisResidentName', residentId] as const;

export function useAnalysisResidentNameQuery(residentId?: string) {
  return useQuery<string | null>({
    queryKey: getAnalysisResidentNameQueryKey(residentId),
    enabled: !!residentId,
    queryFn: async () => {
      const response = await ResidentService.getResident(residentId!);
      const firstName = response.first_name || '';
      const lastName = response.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName.length > 0 ? fullName : null;
    },
    staleTime: STALE_TIME_DETAIL_MS,
  });
}

export type AnalysisDiapersStatQueryInput = {
  timestamp?: number;
  careHomeId?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
};

export const getAnalysisDiapersStatQueryKey = (
  input: AnalysisDiapersStatQueryInput
) =>
  [
    'analysisDiapersStat',
    input.careHomeId,
    input.startDate,
    input.endDate,
    input.stationIds,
    input.residentIds?.join(','),
    input.timestamp,
  ] as const;

function transformDateToIso(dateString: string, isEndDate: boolean): string {
  const parts = dateString.split('.');
  if (parts.length !== 3) return '';

  const [day, month, year] = parts;

  if (isEndDate) {
    const utcDate = new Date(
      Date.UTC(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        23,
        59,
        59,
        999
      )
    );
    return utcDate.toISOString();
  }

  const utcDate = new Date(
    Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      0,
      0,
      0
    )
  );
  return utcDate.toISOString();
}

export function useAnalysisDiapersStatQuery(input: AnalysisDiapersStatQueryInput) {
  return useQuery<number>({
    queryKey: getAnalysisDiapersStatQueryKey(input),
    enabled:
      !!input.timestamp &&
      !!input.careHomeId &&
      !!input.startDate &&
      !!input.endDate &&
      typeof input.stationIds === 'string' &&
      input.stationIds.length > 0 &&
      Array.isArray(input.residentIds),
    queryFn: async () => {
      const startDateIso = transformDateToIso(input.startDate!, false);
      const endDateIso = transformDateToIso(input.endDate!, true);

      return await DiaperService.getDiapersStat({
        carehome_id: input.careHomeId!,
        start_date: startDateIso,
        end_date: endDateIso,
        resident_ids: input.residentIds ?? [],
        station_ids: input.stationIds ? input.stationIds.split(',') : [],
      });
    },
  });
}

export type AnalysisUrineDistributionQueryInput = {
  timestamp?: number;
  careHomeId?: string;
  startDateIso: string;
  endDateIso: string;
  stationIds?: string;
  residentIds?: string[];
  excludeCatheter: boolean;
};

export const getAnalysisUrineLevelDistributionQueryKey = (
  input: AnalysisUrineDistributionQueryInput
) =>
  [
    'analysisUrineLevelDistribution',
    input.careHomeId,
    input.startDateIso,
    input.endDateIso,
    input.stationIds,
    input.residentIds?.join(','),
    input.excludeCatheter,
    input.timestamp,
  ] as const;

export function useAnalysisUrineLevelDistributionQuery(
  input: AnalysisUrineDistributionQueryInput
) {
  const residentIdsParam = input.residentIds?.join(',') ?? '';

  return useQuery<IUrineLevelDistributionItem[]>({
    queryKey: getAnalysisUrineLevelDistributionQueryKey(input),
    enabled:
      !!input.careHomeId &&
      input.careHomeId !== '1' &&
      !!input.stationIds &&
      Array.isArray(input.residentIds),
    queryFn: async () => {
      const params: IUrineLevelDistributionPayload = {
        carehome_id: input.careHomeId!,
        start_date: input.startDateIso,
        end_date: input.endDateIso,
        station_ids: input.stationIds!,
        ...(input.excludeCatheter && { exclude_catheter: true }),
      };

      if ((input.residentIds?.length ?? 0) > 0) {
        params.resident_ids = residentIdsParam;
      }

      return await AnalysisService.getUrineLevelDistribution(params);
    },
  });
}

export const getAnalysisUrineLevelDistributionByTimeQueryKey = (
  input: AnalysisUrineDistributionQueryInput
) =>
  [
    'analysisUrineLevelDistributionByTime',
    input.careHomeId,
    input.startDateIso,
    input.endDateIso,
    input.stationIds,
    input.residentIds?.join(','),
    input.excludeCatheter,
    input.timestamp,
  ] as const;

export function useAnalysisUrineLevelDistributionByTimeQuery(
  input: AnalysisUrineDistributionQueryInput
) {
  const residentIdsParam = input.residentIds?.join(',') ?? '';

  return useQuery<IUrineLevelDistributionByTimeItem[]>({
    queryKey: getAnalysisUrineLevelDistributionByTimeQueryKey(input),
    enabled:
      !!input.careHomeId &&
      input.careHomeId !== '1' &&
      !!input.stationIds &&
      Array.isArray(input.residentIds),
    queryFn: async () => {
      const params: IUrineLevelDistributionPayload = {
        carehome_id: input.careHomeId!,
        start_date: input.startDateIso,
        end_date: input.endDateIso,
        station_ids: input.stationIds!,
        ...(input.excludeCatheter && { exclude_catheter: true }),
      };

      if ((input.residentIds?.length ?? 0) > 0) {
        params.resident_ids = residentIdsParam;
      }

      return await AnalysisService.getUrineLevelDistributionByTime(params);
    },
  });
}
